import React, { useEffect, useState } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CSpinner,
  CTable,
  CTableHead,
  CTableHeaderCell,
  CTableBody,
  CTableRow,
  CTableDataCell,
  CBadge,
} from "@coreui/react";
import { apiClient } from "services/Api";
import ToastNotification from "components/globals/ToastNotification";

const BillingUpdatesEmail = () => {
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [invalidEntries, setInvalidEntries] = useState(0);
  const [emails, setEmails] = useState([]);
  const [groupedRecipients, setGroupedRecipients] = useState([]);
  const [copyLoading, setCopyLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchRecipientsSummary = async (showToastOnError = false) => {
    try {
      const response = await apiClient.get(
        "/clients/billing-updates-email/recipients/"
      );

      setHasPermission(true);
      setRecipientCount(response.data.total_emails || 0);
      setInvalidEntries((response.data.invalid_entries || []).length);
      setEmails(response.data.emails || []);
      setGroupedRecipients(response.data.grouped_recipients || []);
      return response.data;
    } catch (err) {
      console.log(err);
      
      if (err?.response?.status === 403) {
        setHasPermission(false);
        setRecipientCount(0);
        setInvalidEntries(0);
        setEmails([]);
        setGroupedRecipients([]);
      }

      if (showToastOnError) {
        setToast({
          title: "Atualizações de faturação",
          message:
            err?.response?.data?.detail || "Não foi possível obter os emails.",
          type: "error",
        });
      }
      return null;
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchRecipientsSummary(false);
      setLoading(false);
    };
    load();
  }, []);

  const copyEmailsToClipboard = async () => {
    setCopyLoading(true);
    const summary = await fetchRecipientsSummary(true);
    setCopyLoading(false);

    if (!summary) return;

    const recipients = summary.emails || [];
    const count = recipients.length;
    if (!count) {
      setToast({
        title: "Atualizações de faturação",
        message: "Não existem emails válidos para copiar.",
        type: "warning",
      });
      return;
    }

    const clipboardText = recipients.join(";");

    try {
      await navigator.clipboard.writeText(clipboardText);
      setToast({
        title: "Atualizações de faturação",
        message: `${count} email(s) copiado(s) para a área de transferência.`,
        type: "success",
      });
    } catch (err) {
      setToast({
        title: "Atualizações de faturação",
        message: "Não foi possível copiar os emails para a área de transferência.",
        type: "error",
      });
      return;
    }

    const invalidCount = (summary.invalid_entries || []).length;
    if (invalidCount > 0) {
      setToast({
        title: "Atualizações de faturação",
        message: `${invalidCount} entrada(s) inválida(s) foram ignoradas.`,
        type: "warning",
      });
    }
  };

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Email Atualizações de Faturação</h3>
          {loading ? (
            <CSpinner size="sm" />
          ) : hasPermission ? (
            <CButton
              color="primary"
              onClick={copyEmailsToClipboard}
              disabled={copyLoading}
            >
              {copyLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  A obter emails...
                </>
              ) : (
                "Copiar emails"
              )}
            </CButton>
          ) : null}
        </CCardHeader>
        <CCardBody>
          {loading ? (
            <div className="text-medium-emphasis">A carregar...</div>
          ) : hasPermission ? (
            <>
              <p className="mb-2">
                Destinatários válidos encontrados no Airtable:{" "}
                <strong>{recipientCount}</strong>
              </p>
              <p className="mb-0 text-medium-emphasis">
                Entradas inválidas ignoradas: <strong>{invalidEntries}</strong>
              </p>

              <hr />
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Emails por Name</strong>
                <span className="text-medium-emphasis small">
                  Grupos: {groupedRecipients.length}
                </span>
              </div>

              {groupedRecipients.length ? (
                <CTable hover responsive align="middle">
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell>Name</CTableHeaderCell>
                      <CTableHeaderCell>Emails</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {groupedRecipients.map((item, index) => {
                      const rowKey = `${item.name || "sem-name"}-${index}`;
                      return (
                        <CTableRow key={rowKey}>
                          <CTableDataCell>{item.name || "-"}</CTableDataCell>
                          <CTableDataCell>
                            {(item.emails || []).length ? (
                              item.emails.map((email) => (
                                <CBadge
                                  key={`${rowKey}-${email}`}
                                  color="secondary"
                                  className="me-1 mb-1"
                                >
                                  {email}
                                </CBadge>
                              ))
                            ) : (
                              "-"
                            )}
                          </CTableDataCell>
                        </CTableRow>
                      );
                    })}
                  </CTableBody>
                </CTable>
              ) : (
                <div className="text-medium-emphasis small">
                  Sem registos com emails válidos.
                </div>
              )}
            </>
          ) : (
            <p className="mb-0 text-danger">
              Sem permissão para listar/copiar estes emails.
            </p>
          )}
        </CCardBody>
      </CCard>
    </>
  );
};

export default BillingUpdatesEmail;
